import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Copy, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { SITE } from "@/data/site";
import { czk } from "@/lib/price";
import { requestLoginLink, verifyLoginToken, fetchMe, patchMe, memberLogout, formatApiError } from "@/lib/clubApi";
import { usePageMeta } from "@/lib/usePageMeta";

const inputCls = "bg-brand-bg border-brand-border focus-visible:ring-brand-accent text-white h-11";
const STATUS = { new: "Poptávka přijata", confirmed: "Termín potvrzen", done: "Dokončeno", cancelled: "Zrušeno" };
const loc = (k) => SITE.locations.find((l) => l.key === k)?.name || k;

function LoginRequest() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    try {
      await requestLoginLink(email.trim());
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };
  return (
    <div className="min-h-screen bg-brand-bg text-white flex items-center justify-center px-6">
      <form onSubmit={submit} data-testid="member-login-form" className="w-full max-w-sm bg-brand-surface border border-brand-border p-8 space-y-4">
        <div className="text-xs uppercase tracking-[0.3em] text-brand-accent">CARSPA CLUB</div>
        <div className="font-display font-black text-2xl">Můj účet</div>
        {sent ? (
          <p className="text-sm text-white/85" data-testid="member-login-sent">Pokud je e-mail v klubu, poslali jsme na něj přihlašovací odkaz (platí 15 minut). Zkontrolujte i spam.</p>
        ) : (
          <>
            <p className="text-sm text-brand-muted">Bez hesla — pošleme vám jednorázový přihlašovací odkaz.</p>
            <Input data-testid="member-login-email" type="email" required placeholder="Váš e-mail" value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} />
            <button type="submit" data-testid="member-login-submit" className="w-full h-11 bg-brand-accent text-black font-semibold hover:bg-brand-accentHover">Poslat přihlašovací odkaz</button>
          </>
        )}
        <div className="text-xs text-brand-muted">Ještě nejste členem? <Link to="/#vstup" className="text-brand-accent">Vstupte zdarma</Link>.</div>
      </form>
    </div>
  );
}

export default function MyAccount() {
  usePageMeta({ title: "Můj účet — CARSPA CLUB", description: "Členská sekce CARSPA CLUB.", path: "/muj-ucet", noindex: true });
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState(null); // null loading, false anonymous
  const [form, setForm] = useState(null);

  const load = () => fetchMe().then((d) => { setData(d); setForm({ phone: d.member.phone, preferred_location: d.member.preferred_location, vehicle: d.member.vehicle, marketing_opt_in: d.member.marketing_opt_in }); }).catch(() => setData(false));

  useEffect(() => {
    const token = params.get("token");
    if (token) {
      verifyLoginToken(token)
        .then(() => { setParams({}, { replace: true }); load(); })
        .catch((e) => { toast.error(formatApiError(e?.response?.data?.detail, "Odkaz je neplatný nebo vypršel.")); setParams({}, { replace: true }); setData(false); });
    } else load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (data === null) return <div className="min-h-screen bg-brand-bg text-brand-muted flex items-center justify-center">Přihlašuji...</div>;
  if (!data) return <LoginRequest />;

  const { member, offers, reservations, completed_visits, points = 0, point_transactions = [], rewards = [], partners = [] } = data;
  const save = async () => {
    try {
      const m = await patchMe(form);
      setData((d) => ({ ...d, member: m }));
      toast.success("Údaje uloženy.");
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };
  const copyCode = async (code) => {
    try { await navigator.clipboard.writeText(code); toast.success("Kód zkopírován."); }
    catch { toast.message(`Kód: ${code}`); }
  };

  return (
    <div className="min-h-screen bg-brand-bg text-white" data-testid="member-dashboard">
      <header className="border-b border-brand-border">
        <div className="max-w-[1100px] mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="font-display font-black flex items-center gap-2"><span className="w-2 h-2 bg-brand-accent" />CARSPA CLUB</Link>
          <button onClick={async () => { await memberLogout(); setData(false); }} data-testid="member-logout" className="h-9 px-3 border border-brand-border text-xs hover:border-red-400 hover:text-red-400">Odhlásit</button>
        </div>
      </header>
      <main className="max-w-[1100px] mx-auto px-6 py-8 grid lg:grid-cols-12 gap-6">
        {/* card */}
        <div className="lg:col-span-5 space-y-4">
          <div className="relative bg-gradient-to-br from-[#0f0f0f] to-[#1a1a1a] border border-brand-accent/60 p-6 aspect-[1.6] flex flex-col justify-between" data-testid="member-card">
            <div className="flex justify-between text-xs uppercase tracking-[0.3em]"><span className="text-brand-accent">CARSPA CLUB</span><span className="text-brand-muted">Členská karta</span></div>
            <div>
              <div className="font-display font-black text-2xl" data-testid="member-name">{member.name}</div>
              <div className="mt-1 font-mono text-lg text-brand-accent" data-testid="member-number">{member.member_number}</div>
            </div>
            <div className="flex justify-between text-xs text-brand-muted"><span>{loc(member.preferred_location)}</span><span>Člen od {new Date(member.created_at).toLocaleDateString("cs-CZ")}</span></div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <a href="/akce/detailing#reservation" data-testid="member-order-cta" className="text-center h-12 leading-[3rem] bg-brand-accent text-black font-bold hover:bg-brand-accentHover">Objednat další péči</a>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/club/me/card.pdf`} data-testid="member-card-pdf" className="text-center h-12 leading-[3rem] border border-brand-accent text-brand-accent font-semibold hover:bg-brand-accent hover:text-black">Stáhnout kartu (PDF)</a>
          </div>

          <div className="bg-brand-surface border border-brand-accent/40 p-5" data-testid="member-points">
            <div className="flex items-end justify-between gap-4">
              <div>
                <div className="text-[10px] uppercase tracking-[0.25em] text-brand-muted">Vaše body</div>
                <div className="mt-1 font-display font-black text-4xl text-brand-accent leading-none" data-testid="member-points-value">{points}</div>
              </div>
              <div className="text-xs text-brand-muted text-right max-w-[220px]">Body získáváte při návštěvách — ukažte kartu při předání vozu. Za body si vyberete odměnu.</div>
            </div>
            {rewards.length > 0 && (
              <div className="mt-4 space-y-2" data-testid="member-rewards">
                <div className="text-[10px] uppercase tracking-[0.25em] text-brand-muted">Odměny za body</div>
                {rewards.map((r) => {
                  const pct = Math.min(100, Math.round((points / r.cost_points) * 100));
                  return (
                    <div key={r.id} className="text-sm" data-testid={`member-reward-${r.id}`}>
                      <div className="flex justify-between gap-3"><span className="font-semibold">{r.title}</span><span className={points >= r.cost_points ? "text-brand-accent font-semibold" : "text-brand-muted"}>{points >= r.cost_points ? "K uplatnění" : `${r.cost_points} b.`}</span></div>
                      {r.description && <div className="text-xs text-brand-muted">{r.description}</div>}
                      <div className="mt-1 h-1 bg-brand-border"><div className="h-1 bg-brand-accent transition-[width]" style={{ width: `${pct}%` }} /></div>
                    </div>
                  );
                })}
              </div>
            )}
            {point_transactions.length > 0 && (
              <div className="mt-4" data-testid="member-points-history">
                <div className="text-[10px] uppercase tracking-[0.25em] text-brand-muted mb-1">Historie bodů</div>
                <div className="max-h-36 overflow-auto text-xs divide-y divide-brand-border/60">
                  {point_transactions.map((t) => <div key={t.id} className="py-1.5 flex justify-between gap-3"><span><b className={t.delta > 0 ? "text-brand-accent" : "text-red-400"}>{t.delta > 0 ? "+" : ""}{t.delta}</b>{t.reason && <span className="text-brand-muted"> · {t.reason}</span>}{t.reward_title && !t.reason && <span className="text-brand-muted"> · {t.reward_title}</span>}</span><span className="text-brand-muted whitespace-nowrap">{new Date(t.at).toLocaleDateString("cs-CZ")}</span></div>)}
                </div>
              </div>
            )}
          </div>

          <div className="bg-brand-surface border border-brand-border p-5 space-y-3" data-testid="member-settings">
            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-muted">Údaje a nastavení</div>
            <Input data-testid="member-phone" placeholder="Telefon" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className={inputCls} />
            <div className="grid grid-cols-2 gap-2">
              {SITE.locations.map((l) => (
                <button key={l.key} type="button" onClick={() => setForm({ ...form, preferred_location: l.key })} className={`h-10 text-xs border ${form.preferred_location === l.key ? "bg-brand-accent text-black border-brand-accent" : "border-brand-border"}`}>{l.name.replace("Praha ", "")}</button>
              ))}
            </div>
            <div className="grid grid-cols-2 gap-2">
              <Input data-testid="member-vehicle" placeholder="Vozidlo (např. Škoda Superb)" value={form.vehicle.make_model} onChange={(e) => setForm({ ...form, vehicle: { ...form.vehicle, make_model: e.target.value } })} className={inputCls} />
              <Input data-testid="member-plate" placeholder="SPZ" value={form.vehicle.plate} onChange={(e) => setForm({ ...form, vehicle: { ...form.vehicle, plate: e.target.value } })} className={inputCls} />
            </div>
            <label className="flex items-start gap-3 text-xs text-white/80 cursor-pointer">
              <input type="checkbox" data-testid="member-marketing" checked={form.marketing_opt_in} onChange={(e) => setForm({ ...form, marketing_opt_in: e.target.checked })} className="mt-0.5 accent-[#00DF82]" />
              <span>{data.marketing_text}</span>
            </label>
            <button onClick={save} data-testid="member-save" className="h-10 px-4 border border-brand-accent text-brand-accent text-sm hover:bg-brand-accent hover:text-black">Uložit</button>
          </div>
        </div>

        <div className="lg:col-span-7 space-y-4">
          <div className="bg-brand-surface border border-brand-accent/40 p-5" data-testid="member-partners">
            <div className="flex items-center justify-between gap-3 mb-3">
              <div className="text-[10px] uppercase tracking-[0.25em] text-brand-muted">Partnerské výhody</div>
              <div className="text-[10px] uppercase tracking-[0.2em] text-brand-accent">Odemčeno vaší kartou</div>
            </div>
            {partners.length === 0 ? (
              <div className="text-sm text-brand-muted">Zatím připravujeme partnerské výhody — jakmile je spustíme, najdete tu své slevové kódy.</div>
            ) : (
              <div className="space-y-3">
                {partners.map((p) => (
                  <div key={p.id} className="border border-brand-border p-4 bg-brand-bg/40" data-testid={`member-partner-${p.id}`}>
                    <div className="flex items-start gap-3">
                      {p.logo ? <img src={p.logo} alt={p.name} className="w-12 h-12 object-contain bg-white/5 border border-brand-border p-1 shrink-0" /> : null}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-semibold">{p.name}</span>
                          {p.discount && <span className="text-xs font-bold text-black bg-brand-accent px-2 py-0.5" data-testid={`member-partner-discount-${p.id}`}>{p.discount}</span>}
                          {p.category && <span className="text-[10px] uppercase tracking-wide text-brand-muted border border-brand-border px-1.5 py-0.5">{p.category}</span>}
                        </div>
                        {p.description && <div className="text-xs text-brand-muted mt-1">{p.description}</div>}
                        {p.conditions && <div className="text-[11px] text-brand-muted/80 mt-1">{p.conditions}</div>}
                      </div>
                    </div>
                    <div className="mt-3 flex items-center gap-2 flex-wrap">
                      {p.code && (
                        <button onClick={() => copyCode(p.code)} data-testid={`member-partner-code-${p.id}`} className="inline-flex items-center gap-2 h-9 px-3 border border-brand-accent text-brand-accent font-mono text-sm hover:bg-brand-accent hover:text-black transition-colors">
                          {p.code} <Copy size={13} />
                        </button>
                      )}
                      {p.url && <a href={p.url} target="_blank" rel="noreferrer" data-testid={`member-partner-link-${p.id}`} className="inline-flex items-center gap-1.5 h-9 px-3 border border-brand-border text-xs hover:border-brand-accent hover:text-brand-accent transition-colors">Web partnera <ExternalLink size={12} /></a>}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          <div className="bg-brand-surface border border-brand-border p-5" data-testid="member-offers">
            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-muted mb-3">Aktivní nabídky</div>
            {offers.length === 0 ? <div className="text-sm text-brand-muted">Zatím žádné aktivní nabídky — jakmile je připravíme, uvidíte je tady první.</div> :
              offers.map((o) => (
                <div key={o.id} className="py-3 border-b border-brand-border last:border-0"><div className="font-semibold">{o.title}</div><div className="text-xs text-brand-muted mt-1">{o.conditions}</div></div>
              ))}
          </div>
          <div className="bg-brand-surface border border-brand-border p-5" data-testid="member-reservations">
            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-muted mb-3">Poptávky a rezervace</div>
            {reservations.length === 0 ? <div className="text-sm text-brand-muted">Zatím žádné poptávky.</div> :
              reservations.map((r) => (
                <div key={r.id} className="py-3 border-b border-brand-border last:border-0 flex justify-between gap-4 text-sm">
                  <div><div className="font-semibold">{loc(r.location)} · {new Date(r.created_at).toLocaleDateString("cs-CZ")}</div><div className="text-xs text-brand-muted">Program 4 v 1{r.bundles?.length || r.addons?.length ? " + doplňky" : ""} · {czk(r.total_price)}</div></div>
                  <span className={`text-xs self-start px-2 py-1 border ${r.status === "done" ? "border-white/40" : r.status === "cancelled" ? "border-red-400 text-red-400" : "border-brand-accent text-brand-accent"}`}>{STATUS[r.status] || r.status}</span>
                </div>
              ))}
          </div>
          <div className="bg-brand-surface border border-brand-border p-5" data-testid="member-visits">
            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-muted mb-3">Dokončené návštěvy</div>
            {completed_visits.length === 0 ? <div className="text-sm text-brand-muted">Zatím žádná dokončená návštěva.</div> :
              completed_visits.map((r) => <div key={r.id} className="py-2 text-sm flex justify-between"><span>{loc(r.location)}</span><span className="text-brand-muted">{new Date(r.completed_at || r.created_at).toLocaleDateString("cs-CZ")}</span></div>)}
          </div>
        </div>
      </main>
    </div>
  );
}
